import { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { BiSupport } from "react-icons/bi";
import { FiBarChart2, FiChevronsLeft, FiChevronsRight, FiUsers, FiX } from "react-icons/fi";
import { IoExitOutline, IoPersonCircleOutline, IoSettingsOutline } from "react-icons/io5";
import { LuLayoutDashboard } from "react-icons/lu";
import {
  MdAttachMoney,
  MdOutlineRestaurantMenu,
  MdOutlineRoomService,
  MdOutlineTableRestaurant,
} from "react-icons/md";
import { UserContext } from "../context/UserContext";
import { FoodServiceContext } from "../context/FoodServiceContext";
import { ORDER_STATUS_TABS } from "../services/orderStatusModel";
import { formatPersianNumber } from "../utils/persianNumber";
import { getUnreadTicketCount } from "../api/support";

const SideBar = ({ isCollapsed, onToggleCollapse, isMobileOpen, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logoutUser } = useContext(UserContext);
  const { orders } = useContext(FoodServiceContext);

  const pendingOrderCount = orders.filter((order) => order.status === "pending").length;

  // Support replies the restaurant hasn't read yet; re-checked on navigation.
  const [unreadTickets, setUnreadTickets] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    getUnreadTicketCount()
      .then((count) => !cancelled && setUnreadTickets(count))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  // Reservations are still under development: it stays visible in the nav,
  // but its route renders an "under development" notice instead of the real
  // page (see routes/ProtectedRoutes.jsx).
  const menuItems = [
    { label: "داشبورد", icon: LuLayoutDashboard, path: "/dashboard", exact: true },
    { label: "رزروها", icon: MdOutlineTableRestaurant, path: "/dashboard/reservations" },
    {
      label: "سفارش‌ها",
      icon: MdOutlineRoomService,
      path: "/dashboard/orders",
      badge: pendingOrderCount || null,
      childParam: "status",
      children: ORDER_STATUS_TABS.map(({ label, value }) => ({
        label,
        value: value === "all" ? null : value,
        to: value === "all" ? "/dashboard/orders" : `/dashboard/orders?status=${value}`,
      })),
    },
    {
      label: "منو",
      icon: MdOutlineRestaurantMenu,
      path: "/dashboard/menu",
      matches: ["/dashboard/menu", "/dashboard/edit-menu", "/dashboard/panel", "/dashboard/menu-settings"],
    },
    { label: "مشتریان", icon: FiUsers, path: "/dashboard/club" },
    { label: "مالی", icon: MdAttachMoney, path: "/dashboard/pricing" },
    { label: "گزارش‌ها", icon: FiBarChart2, path: "/dashboard/report" },
    { label: "پشتیبانی", icon: BiSupport, path: "/dashboard/support", badge: unreadTickets || null },
    {
      label: "پروفایل",
      icon: IoPersonCircleOutline,
      path: `/dashboard/profile/${user?._id || ""}`,
      matches: ["/dashboard/profile"],
    },
    {
      label: "تنظیمات",
      icon: IoSettingsOutline,
      path: `/dashboard/settings/${user?._id || ""}`,
      matches: ["/dashboard/settings"],
    },
  ];

  const isItemActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    const paths = item.matches || [item.path];
    return paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  };

  // The sub-list for an item isn't a separate open/close toggle — it simply
  // shows up once you're actually on that item's page, and goes away again
  // once you navigate elsewhere. Clicking the parent link is what reveals it.
  const isChildActive = (item, child) => {
    if (location.pathname !== item.path) return false;
    const currentValue = new URLSearchParams(location.search).get(item.childParam) || null;
    return currentValue === child.value;
  };

  const handleLogout = () => {
    logoutUser();
    navigate("/auth/restaurant", { replace: true });
  };

  const renderNavigation = ({ collapsed = false, mobile = false } = {}) => (
    <nav className="flex flex-1 flex-col gap-1 px-2 py-3" aria-label="ناوبری پنل رستوران">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const active = isItemActive(item);
        const hasChildren = Boolean(item.children?.length) && !collapsed;
        const showChildren = hasChildren && active;

        return (
          <div key={item.label}>
            <NavLink
              to={item.path}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              aria-current={active ? "page" : undefined}
              onClick={mobile ? onCloseMobile : undefined}
              className={`group relative flex min-h-11 items-center rounded-xl text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                collapsed ? "justify-center px-2" : "gap-3 px-3"
              } ${
                active
                  ? "bg-[#21453C] text-white"
                  : "text-[#A9C2BA] hover:bg-[#1C3B33] hover:text-white"
              }`}
            >
              {active && <span className="absolute inset-y-2 right-0 w-1 rounded-l-full bg-primary" aria-hidden="true" />}
              <Icon className={`shrink-0 text-xl ${active ? "text-white" : "text-[#7FA398] group-hover:text-white"}`} aria-hidden="true" />
              {collapsed && item.badge > 0 && (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              )}
              {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
              {!collapsed && item.badge > 0 && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
                  {formatPersianNumber(item.badge)}
                </span>
              )}
              {collapsed && !mobile && (
                <span
                  role="tooltip"
                  className="pointer-events-none absolute right-full z-50 mr-2 whitespace-nowrap rounded-lg bg-accent px-2.5 py-1.5 text-xs text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                  {item.label}
                </span>
              )}
            </NavLink>

            {showChildren && (
              <div className="mr-5 mt-1 flex flex-col gap-0.5 border-r border-white/10 pr-3">
                {item.children.map((child) => {
                  const childActive = isChildActive(item, child);
                  return (
                    <NavLink
                      key={child.label}
                      to={child.to}
                      onClick={mobile ? onCloseMobile : undefined}
                      aria-current={childActive ? "page" : undefined}
                      className={`flex min-h-9 items-center rounded-lg px-3 text-sm transition-colors ${
                        childActive ? "font-bold text-white" : "text-[#A9C2BA] hover:text-white"
                      }`}
                    >
                      {child.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      <aside
        className={`fixed right-0 top-0 z-30 hidden h-screen flex-col bg-[#16302A] transition-[width] duration-200 md:flex ${
          isCollapsed ? "w-20" : "w-64"
        }`}
        aria-label="نوار کناری پنل رستوران"
      >
        <div className={`flex h-16 shrink-0 items-center px-3 ${isCollapsed ? "justify-center" : "justify-between"}`}>
          <NavLink
            to="/dashboard"
            className="flex min-w-0 items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="داشبورد میزبان"
          >
            <img src="/images/mizban.png" alt="میزبان" className={`brightness-0 invert ${isCollapsed ? "h-8 w-11 object-contain" : "h-9 w-24 object-contain"}`} />
          </NavLink>
          {!isCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="rounded-lg p-2 text-[#A9C2BA] transition-colors hover:bg-[#1C3B33] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="جمع کردن نوار کناری"
              title="جمع کردن نوار کناری"
            >
              <FiChevronsRight className="text-lg" aria-hidden="true" />
            </button>
          )}
        </div>

        {renderNavigation({ collapsed: isCollapsed })}

        <div className="border-t border-white/10 p-2">
          {isCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="mb-1 flex min-h-10 w-full items-center justify-center rounded-xl text-[#A9C2BA] transition-colors hover:bg-[#1C3B33] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="باز کردن نوار کناری"
              title="باز کردن نوار کناری"
            >
              <FiChevronsLeft className="text-lg" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            title={isCollapsed ? "خروج" : undefined}
            className={`group relative flex min-h-11 w-full items-center rounded-xl text-sm font-medium text-[#F28B82] transition-colors hover:bg-[#1C3B33] focus:outline-none focus:ring-2 focus:ring-red-300/40 ${
              isCollapsed ? "justify-center px-2" : "gap-3 px-3"
            }`}
          >
            <IoExitOutline className="shrink-0 text-xl" aria-hidden="true" />
            {!isCollapsed && <span>خروج</span>}
            {isCollapsed && (
              <span role="tooltip" className="pointer-events-none absolute right-full z-50 mr-2 whitespace-nowrap rounded-lg bg-accent px-2.5 py-1.5 text-xs text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                خروج
              </span>
            )}
          </button>
        </div>
      </aside>

      {isMobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/35"
            onClick={onCloseMobile}
            aria-label="بستن منوی ناوبری"
            tabIndex={-1}
          />
          <aside
            id="mobile-dashboard-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="منوی پنل رستوران"
            className="absolute right-0 top-0 flex h-full w-[min(18rem,86vw)] flex-col bg-[#16302A] shadow-xl"
          >
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
              <img src="/images/mizban.png" alt="میزبان" className="h-9 w-24 object-contain brightness-0 invert" />
              <button
                type="button"
                onClick={onCloseMobile}
                className="rounded-lg p-2 text-[#A9C2BA] hover:bg-[#1C3B33] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                aria-label="بستن منو"
              >
                <FiX className="text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {renderNavigation({ mobile: true })}
              <div className="border-t border-white/10 p-3">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-[#F28B82] hover:bg-[#1C3B33] focus:outline-none focus:ring-2 focus:ring-red-300/40"
                >
                  <IoExitOutline className="text-xl" aria-hidden="true" />
                  خروج از حساب
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default SideBar;
